import Service from "./Service";
import {
  getCompanyCookie,
  checkIfLocaleIsCSE,
  isEmptyResponse,
  calculateAverageRating,
  calculateAverageReview,
} from "@/utils/utils";
import { SearchEngineMethods } from "../enums/searchEngineMethods";
import { Packages } from "../enums/packages";
import {
  AvailabilitiesResponse,
  StartDatesGetRequestData,
  EndDatesGetRequestData,
  AvailabilitiesGetRequestData,
  RelatedPackagesGetRequestData,
  PackagesResponse,
  PackageResponse,
} from "@/types/responses";

export default class SearchEngineService extends Service {
  constructor() {
    super();
    this.serviceUrl = `https://api.travelski.com/se`;
    // this.serviceUrl = `https://api-migdal.travelfactory.fr/dev/se`

    this.mandatoryFields = {
      getStartDates: ["liheId", "packCode", "month", "locale"],
      getEndDates: ["liheId", "packCode", "month", "startDate", "locale"],
      getAvailabilities: ["locale", "packCode", "liheId", "season"],
      getLiheNearbyData: ["locale"],
      getRelatedPackages: [], // requestData: RelatedPackagesGetRequestData and methodToUse: SearchEngineMethods
    };
  }

  async getStartDates(
    requestData: StartDatesGetRequestData
  ): Promise<string[]> {
    this.checkMandatoryFields(requestData, "getStartDates");
    const { liheId, packCode, month, locale } = requestData;
    const headers = { "accept-language": locale.replace("_", "-") };
    const params = {
      liheId,
      packCode,
      month,
      ...(checkIfLocaleIsCSE(locale) && { company: getCompanyCookie() }),
    };
    const url = `${this.serviceUrl}/planning/monthavailabilities/start`;
    const { data } = await this.axios.get(url, { params, headers });
    return data;
  }

  async getEndDates(requestData: EndDatesGetRequestData): Promise<string[]> {
    this.checkMandatoryFields(requestData, "getEndDates");
    const { liheId, packCode, month, locale, startDate } = requestData;
    const headers = { "accept-language": locale.replace("_", "-") };

    const params = {
      liheId,
      packCode,
      month,
      startDate,
      ...(checkIfLocaleIsCSE(locale) && { company: getCompanyCookie() }),
    };
    const url = `${this.serviceUrl}/planning/monthavailabilities/end`;
    const { data } = await this.axios.get(url, { params, headers });
    return data;
  }

  async getAvailabilities(
    requestData: AvailabilitiesGetRequestData
  ): Promise<AvailabilitiesResponse> {
    this.checkMandatoryFields(requestData, "getAvailabilities");
    const { locale, packCode, liheId, season, startDate, endDate } =
      requestData;
    const headers = { "accept-language": locale.replace("_", "-") };
    const params = {
      packCode,
      liheId,
      season,
      startDate,
      endDate,
      ...(checkIfLocaleIsCSE(locale) && { company: getCompanyCookie() }),
    };

    const url = `${this.serviceUrl}/planning/availabilities/`;
    const { data } = await this.axios.get(url, { headers, params });

    // If response is empty, try the alternative season
    if (!data || Object.keys(data).length === 0) {
      const alternativeSeason = season === 'E' ? 'H' : 'E';
      const alternativeParams = {
        ...params,
        season: alternativeSeason
      };
      const { data: alternativeData } = await this.axios.get(url, { headers, params: alternativeParams });
      return alternativeData;
    }

    return data;
  }

  /**
   * Retrieves related packages based on the given `requestData` and `methodToUse`.
   *
   * @param {RelatedPackagesGetRequestData} requestData - The request data containing information like packCode.
   * @param {SearchEngineMethods} methodToUse - The method to call for fetching data.
   * @returns {Object} - If 'packcode' is in 'mapPackCodes', and a response exist for it, return the combined response based on the related packages.
   */
  async getRelatedPackages(
    requestData: RelatedPackagesGetRequestData,
    methodToUse: SearchEngineMethods
  ): Promise<PackageResponse> {
    const packCodesArray: Packages[] | null = this.mapPackCodes(
      requestData.packCode
    );

    if (!packCodesArray) {
        // Explicitly cast `requestData` based on `methodToUse`
        if (methodToUse === SearchEngineMethods.GET_END_DATES) {
          return this[methodToUse](requestData as EndDatesGetRequestData);
        }
        if (methodToUse === SearchEngineMethods.GET_AVAILABILITIES) {
          return this[methodToUse](requestData as AvailabilitiesGetRequestData);
        }
    }
        
    const responses: PackagesResponse = await this.getResponses(
      requestData,
      packCodesArray!,
      methodToUse
    );
    
    return this.buildCombinedResponse(responses, methodToUse);
  }

  async getLiheNearbyData(
    requestData: { locale: string; size?: number },
    payload: any,
    size = 20
  ) {
    this.checkMandatoryFields(requestData, "getLiheNearbyData");
    const headers = {
      "content-type": "application/json",
      "accept-language": requestData.locale.replace("_", "-"),
    };
    const { locale } = requestData;
    const params = {
      size: requestData.size || size,
      ...(checkIfLocaleIsCSE(locale) && { company: getCompanyCookie() }),
    };
    const body = JSON.stringify(payload);
    const url = `${this.serviceUrl}/search/product/map`;
    const { data } = await this.axios.post(url, body, { headers, params });
    return data;
  }

  /**
   * Maps a given packCode to an array of related packCodes.
   *
   * @param {Packages} packCode - The pack code to map.
   * @returns {string[] || null} - An array containing related pack codes or null for the other packCodes
   */
  mapPackCodes(packCode: Packages): Packages[] | null {
    const packMapping = new Map([
      [Packages.PFPCS, [Packages.PFPCS, Packages.PFP]],
      [Packages.PFP, [Packages.PFPCS, Packages.PFP]],
      [Packages.PFCS, [Packages.PFCS, Packages.PF]],
      [Packages.PF, [Packages.PFCS, Packages.PF]],
    ]);

    return packMapping.get(packCode) || null; // null for other packCodes
  }

  /**
   * Fetches responses for the two packCodes related to the given request data.
   *
   * @param {Object} requestData - The request data to pass to the method.
   * @param {string[]} packCodesArray - An array of packCodes to fetch responses for.
   * @param {SearchEngineMethods} methodToUse - The method to use for fetching data.
   * @returns {Object} - An object containing the responses for both packCodes.
   */
  async getResponses(
    requestData: RelatedPackagesGetRequestData,
    packCodesArray: Packages[],
    methodToUse: SearchEngineMethods
  ): Promise<PackagesResponse> {
    const requestDataOne: RelatedPackagesGetRequestData = {
      ...requestData,
      packCode: packCodesArray[0],
    };
    const requestDataTwo: RelatedPackagesGetRequestData = {
      ...requestData,
      packCode: packCodesArray[1],
    };

    let packOneResponse:PackageResponse = {}
    let packTwoResponse: PackageResponse= {}

    if(methodToUse === SearchEngineMethods.GET_AVAILABILITIES) {
        packOneResponse = await this[methodToUse](requestDataOne as AvailabilitiesGetRequestData)
        packTwoResponse = await this[methodToUse](requestDataTwo as AvailabilitiesGetRequestData)
    }

    if(methodToUse === SearchEngineMethods.GET_END_DATES) {
        packOneResponse = await this[methodToUse](requestDataOne as EndDatesGetRequestData)
        packTwoResponse = await this[methodToUse](requestDataTwo as EndDatesGetRequestData)
    }

    return { packOneResponse, packTwoResponse };
  }

  /**
   * Builds the combined response based on the responses of the two packCodes and the method used.
   *
   * @param {Object} responses - An object containing responses for both packCodes.
   * @param {SearchEngineMethods} methodToUse - The method used to determine how to combine the responses.
   * @returns {Object} - The combined response or the relevant responde, in case one of responses are empty.
   */
  buildCombinedResponse(
    responses: PackagesResponse,
    methodToUse: SearchEngineMethods
  ): PackageResponse {
    const { packOneResponse, packTwoResponse } = responses;

    if (isEmptyResponse(packTwoResponse)) return packOneResponse;
    if (isEmptyResponse(packOneResponse)) return packTwoResponse;

    const methodHandler = this.getMethodHandler(methodToUse);
    if (methodHandler) {
      return methodHandler(packOneResponse, packTwoResponse);
    } 
    return {};
  }

  /**
   * Gets the handler function for the specified method.
   *
   * @param {SearchEngineMethods} methodToUse - The method used to determine the appropriate handler.
   * @returns {Function} - The handler function for the specified method.
   */
  getMethodHandler(methodToUse: SearchEngineMethods): (...args: any[]) => any {
    const handlers: Record<string, (...args: any[]) => any> = {
      [SearchEngineMethods.GET_END_DATES]: this.mergeEndDates,
      [SearchEngineMethods.GET_AVAILABILITIES]: this.mergeAvailabilities,
    };

    return handlers[methodToUse];
  }

  mergeEndDates(packOneDates: string[], packTwoDates: string[]) {
    return [...(packOneDates || []), ...(packTwoDates || [])];
  }

  mergeAvailabilities(packOne:AvailabilitiesResponse, packTwo:AvailabilitiesResponse)  {
    return {
        availablePackages: [...(packOne.availablePackages || []), ...(packTwo.availablePackages || [])],
        averageRatingPerLihe: calculateAverageRating(packOne, packTwo),
        codePack: [...(packOne.codePack || []), ...(packTwo.codePack || [])],
        endDates: {
            dates: [
                ...(packOne.endDates?.dates || []),
                ...(packTwo.endDates?.dates || []),
            ], 

            endDate: packOne.endDates?.endDate ? packOne.endDates?.endDate : packTwo.endDates?.endDate,
            startDate: packOne.endDates?.startDate ? packOne.endDates?.startDate : packTwo.endDates?.startDate,
        },
        prestations: [...(packOne.prestations || []), ...(packTwo.prestations || [])],
        reviewNumberPerLihe: calculateAverageReview(packOne, packTwo),
        startDates: [...(packOne.startDates || []), ...(packTwo.startDates || [])],
        suggestions: [...(packOne.suggestions || []), ...(packTwo.suggestions || [])],
    };
  }
}